import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Close';

import Input from '../shared/Input';
import Drawer from '../shared/Drawer';
import Autocomplete from '../shared/Autocomplete';

import userRoles from '../../constants/userRoles';
import countries from '../../constants/countries';

const countriesList = countries.map((c) => c.name);

const ManufacturerModal = (props) => {
  const {
    mode = 'create',
    open,
    onClose,
    onSubmit,
    loading,
    usersList = [],
    initialValues,
  } = props;

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [postCode, setPostCode] = useState('');
  const [country, setCountry] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open && mode === 'update') {
      setName(initialValues?.name || '');
      setAddress(initialValues?.address || '');
      setCountry(initialValues?.country || '');
      setState(initialValues?.state || '');
      setPostCode(initialValues?.postCode || '');
      setUsers(initialValues?.users?.map((uid) => usersList.find((u) => u.uid === uid)) || []);
    }
  }, [open, mode, initialValues, usersList]);

  const resetValues = () => {
    setName('');
    setCountry(null);
    setAddress( '');
    setState('');
    setPostCode('');
    setUsers([]);
  };

  const handleSubmit = async () => {
    const formObj = {
      name: name || '',
      address,
      country,
      state,
      postCode,
      users: users?.length ? users.map((u) => u.uid) : [],
    };
    await onSubmit(formObj);
    resetValues();
  };

  const isDisabled = () => {
    return !name || !country;
  };

  const onUserSelect = (value) => {
    if (value && !users?.find((u) => u.uid === value?.uid)) {
      setUsers([...users, value]);
    }
  };

  const onUserRemove = (uid) => {
    setUsers([...users].filter((u) => u.uid !== uid));
    setSearch('');
  }

  const usersOptions = useMemo(() => {
    return usersList.filter((u) => u.role === userRoles.MANUFACTURER.name && !users?.find((user) => user?.uid === u?.uid));
  }, [usersList, users]);

  return (
    <Drawer
      open={open}
      onClose={() => {
        resetValues();
        onClose();
      }}
      title={mode === 'update' ? 'Edit Manufacturer' : 'Create Manufacturer'}
      subtitle="Enter manufacturer details"
      submitText={mode === 'update' ? 'Save Changes' : 'Create'}
      disabled={isDisabled()}
      loading={loading}
      onSubmit={handleSubmit}
    >
      <Box sx={{ minWidth: { xs: 'auto', md: '560px' } }}>
        <Grid container spacing={1} sx={{ maxWidth: '600px' }}>
          <Grid size={{ xs: 12 }}>
            <Input
              id="name"
              type="text"
              placeholder=""
              value={name}
              onChange={(e) => setName(e.target.value)}
              label="Manufacturer Name"
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              value={country}
              onChange={setCountry}
              options={countriesList}
              label="Country"
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Input
              id="address"
              type="text"
              placeholder=""
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              label="Address"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              id="state"
              type="text"
              placeholder=""
              value={state}
              onChange={(e) => setState(e.target.value)}
              label="State"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              id="postCode"
              type="text"
              placeholder=""
              value={postCode}
              onChange={(e) => setPostCode(e.target.value)}
              label="Post Code"
            />
          </Grid>


          <Divider sx={{ width: '100%', mt: 2, mb: 1 }} />

          <Grid size={{ xs: 12 }}>
            <Autocomplete
              value={null}
              inputValue={search}
              onInputChange={(e, v, reason) => {
                if (reason === 'reset') return;
                setSearch(v);
              }}
              onChange={(selected) => {
                onUserSelect(selected);
                setSearch('');
              }}
              placeholder="Search"
              label="Members"
              options={usersOptions}
              getOptionLabel={(option) => `${option?.firstName} ${option?.lastName} (${option?.email})`}
              groupBy={(option) =>
                option?.lastName?.[0]?.toUpperCase() ||
                option?.firstName?.[0]?.toUpperCase()
              }
            />

            <Box sx={{ mt: 2 }}>
              {users.map((user) => (
                <Box key={user?.uid} sx={{ display: 'flex', mb: 0.5, alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 14 }}>
                      {`${user?.firstName} ${user?.lastName}`}
                    </Typography>
                    <Typography sx={{ fontSize: 14, ml: 1, color: '#A4A7AE' }}>
                      {user?.email}
                    </Typography>
                  </Box>
                  <IconButton aria-label="delete" color="error" onClick={() => onUserRemove(user?.uid)} size="small">
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
};

ManufacturerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default ManufacturerModal;
